import {
  AutoRecipeParameters,
  AutoRecipePeriodLayout,
} from "store/api/generatedApi";
import {
  inventoryPeriodGroups,
  InventoryTargetCopyScope,
} from "./ruleParameters";

export interface PeriodCountRange {
  minimum: number;
  maximum: number | null;
}

export interface PeriodLayout {
  deployable: PeriodCountRange;
  forecast: PeriodCountRange;
}

const fixedCount = (count: number): PeriodCountRange => ({
  minimum: count,
  maximum: count,
});

export const resolvePeriodLayout = ({
  layout,
  parameters,
}: {
  layout: AutoRecipePeriodLayout;
  parameters: AutoRecipeParameters;
}): PeriodLayout => {
  const planning = parameters.planning as Record<string, unknown> | null;
  const count = (field: string, minimum: number) => {
    const amount: unknown = planning?.[field];
    return typeof amount === "number" && Number.isFinite(amount)
      ? Math.max(minimum, Math.floor(amount))
      : minimum;
  };
  switch (layout) {
    case "single_period":
      return { deployable: fixedCount(1), forecast: fixedCount(0) };
    case "deployable_sequence":
      return {
        deployable: { minimum: 1, maximum: null },
        forecast: fixedCount(0),
      };
    case "first_deployable_sequence":
      return {
        deployable: fixedCount(1),
        forecast: { minimum: 0, maximum: null },
      };
    case "schedule_forecast":
      return {
        deployable: fixedCount(1),
        forecast: fixedCount(Number(count("forecast_days", 0) > 0)),
      };
    case "calendar_forecast": {
      const schedules = [
        count("weekday_schedule_days", 1),
        count("weekend_schedule_days", 1),
      ];
      return {
        deployable: {
          minimum: Math.min(...schedules),
          maximum: Math.max(...schedules),
        },
        forecast: fixedCount(count("num_months_forecast", 0)),
      };
    }
    case "weekly_forecast":
      return {
        deployable: fixedCount(1),
        forecast: fixedCount(
          Number(count("num_weeks_medium_term_forecast", 0) > 0) +
            Number(count("num_weeks_long_term_forecast", 0) > 0)
        ),
      };
  }
};

export const maximumPreviewPeriods = 24;

export const previewPeriodCount = (range: PeriodCountRange) =>
  Math.min(maximumPreviewPeriods, range.maximum ?? Math.max(3, range.minimum));

export const clampPreviewPeriodCount = (
  count: number,
  range: PeriodCountRange
) =>
  Math.max(
    Math.min(range.minimum, maximumPreviewPeriods),
    Math.min(
      count,
      range.maximum ?? maximumPreviewPeriods,
      maximumPreviewPeriods
    )
  );

export const hasLimitedPeriodPreview = (layout: PeriodLayout) =>
  [layout.deployable, layout.forecast].some(
    ({ minimum, maximum }) =>
      minimum > maximumPreviewPeriods ||
      (maximum !== null && maximum > maximumPreviewPeriods)
  );

export const inventoryGroupsForLayout = (layout: PeriodLayout) =>
  inventoryPeriodGroups
    .filter(({ isDeployable, position }) => {
      const maximum = (isDeployable ? layout.deployable : layout.forecast)
        .maximum;
      return (
        maximum === null ||
        maximum >= (position === "first" ? 1 : position === "last" ? 2 : 3)
      );
    })
    .map((definition) => ({
      ...definition,
      label:
        layout.forecast.maximum === 0
          ? {
              first: "autoFirstPeriod",
              other: "autoOtherPeriods",
              last: "autoLastPeriod",
            }[definition.position]
          : definition.label,
    }));

export const inventoryCopyScopes = (
  layout: PeriodLayout
): InventoryTargetCopyScope[] =>
  layout.forecast.maximum === 0 ? ["all"] : ["all", "deployable", "forecast"];
